import { cognitionApi } from '../../services/cognitionApi';

// Training records engagement and adaptive difficulty state only. It must never
// contribute to an assessment score. Keeping the two drivers separate is what
// makes that invariant enforceable at one boundary instead of scattered
// through the UI.
export function createTrainingDriver({ gameId, clientVersion = 'web-1.0' }) {
  let sessionId = null;

  return {
    mode: 'training',

    // Training trials end this long after an accepted response rather than
    // waiting out the rest of the response window. Fixed pacing exists so a
    // trial's length cannot leak whether the user pressed; that only matters
    // for scored instruments, and training is never scored. Without this a
    // 100-trial session is mostly the player staring at a stimulus they have
    // already answered. Assessments deliberately do not set this field.
    advanceOnResponseMs: 250,

    // Training is never scored, so a per-trial correct/incorrect signal costs
    // nothing and a run without one gives the player nothing to learn from.
    // Tasks that cannot compute correctness client-side opt out individually
    // via `feedbackAvailable: false`.
    feedbackOnEveryTrial: true,

    async start() {
      const res = await cognitionApi.startTrainingSession({ gameId, clientVersion });
      sessionId = res.sessionId;
      return { taskId: res.taskId, blockCount: res.blockCount, block: res.block };
    },

    async submitBlock(blockIndex, trials) {
      const res = await cognitionApi.submitTrainingBlock({
        sessionId,
        blockIndex,
        trials,
        clientVersion,
      });
      // Adaptive fields are optional on the response. Returning an object of
      // undefineds would still be truthy, so the runner would push a blank
      // stats entry for every block and the UI would render empty rows.
      const hasStats = res.direction != null || res.nextLevel != null;
      return {
        nextBlock: res.nextBlock,
        complete: res.sessionComplete,
        stats: hasStats
          ? {
              accuracy: res.accuracy,
              direction: res.direction,
              nextLevel: res.nextLevel,
            }
          : null,
        // Only the FINAL block's response carries this — every other block's
        // response simply omits the field. Passed through as-is (never
        // defaulted to an object of nulls) so the runner can tell "no summary
        // yet" apart from "a summary with nothing scored".
        sessionSummary: res.sessionSummary ?? null,
      };
    },

    async abandon() {
      if (sessionId) await cognitionApi.abandonTrainingSession({ sessionId });
    },
  };
}
